import { motion } from "framer-motion";
import { ArrowRight, UtensilsCrossed } from "lucide-react";
import { useLang } from "@/i18n";

const HERO_IMG = "https://images.unsplash.com/photo-1553909489-2542a1068437?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxyYW1lbiUyMGJvd2wlMjBkYXJrJTIwcGhvdG9ncmFwaHl8ZW58MHx8fHwxNzg0MDM1NjEyfDA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const { t } = useLang();
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden grain"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Ramen fumante"
          className="w-full h-full object-cover scale-105"
          loading="eager"
          fetchPriority="high"
        />
        {/* dark cinematic overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/40" />
        <div className="absolute inset-0 bg-black/40" />
      </div>

      {/* Steam particles */}
      <div className="absolute inset-0 pointer-events-none">
        <span className="steam-particle animate-steam" style={{ left: "22%", animationDelay: "0s" }} />
        <span className="steam-particle animate-steam" style={{ left: "38%", animationDelay: "1.4s" }} />
        <span className="steam-particle animate-steam" style={{ left: "55%", animationDelay: "0.7s" }} />
        <span className="steam-particle animate-steam" style={{ left: "70%", animationDelay: "2.1s" }} />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center">
        <div className="max-w-7xl w-full mx-auto px-6 md:px-10 pt-28 pb-24">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-8" data-testid="hero-overline">
              <span className="h-px w-10 bg-gold" />
              <span className="text-xs md:text-sm uppercase tracking-[0.32em] text-gold font-medium">
                {t("hero_overline")}
              </span>
            </div>

            <h1
              data-testid="hero-title"
              className="font-display text-5xl md:text-6xl lg:text-7xl font-semibold leading-[1.02] tracking-tight text-balance"
            >
              {t("hero_title_1")}<br />
              <span className="italic font-light text-white/90">{t("hero_title_asia")}</span> {t("hero_title_at")}{" "}
              <span className="relative inline-block">
                Catania
                <span className="absolute -bottom-2 left-0 right-0 h-[3px] bg-gold" />
              </span>
              .
            </h1>

            <p
              data-testid="hero-subtitle"
              className="mt-8 text-lg md:text-xl text-white/75 leading-relaxed max-w-2xl font-light"
            >
              {t("hero_subtitle")}
            </p>

            <div className="mt-12 flex flex-col sm:flex-row gap-4">
              <button
                data-testid="hero-cta-book"
                onClick={() => scrollTo("prenota")}
                className="group inline-flex items-center justify-center gap-3 bg-ember hover:bg-emberDeep text-white px-8 py-4 rounded-full font-medium tracking-wide transition-colors duration-300 shadow-[0_10px_40px_-10px_rgba(200,16,46,0.6)]"
              >
                {t("hero_cta_book")}
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>

              <button
                data-testid="hero-cta-menu"
                onClick={() => scrollTo("menu")}
                className="group inline-flex items-center justify-center gap-3 border border-white/25 hover:border-white text-white hover:bg-white hover:text-ink px-8 py-4 rounded-full font-medium tracking-wide transition-colors duration-300"
              >
                <UtensilsCrossed size={18} />
                {t("hero_cta_menu")}
              </button>
            </div>

            {/* Stat strip */}
            <div className="mt-16 grid grid-cols-3 max-w-lg gap-6 border-t border-white/10 pt-8" data-testid="hero-stats">
              <div>
                <div className="font-display text-2xl md:text-3xl text-gold">6</div>
                <div className="text-xs uppercase tracking-widest text-white/60 mt-1">{t("hero_stat_categorie")}</div>
              </div>
              <div>
                <div className="font-display text-2xl md:text-3xl text-gold">24h</div>
                <div className="text-xs uppercase tracking-widest text-white/60 mt-1">{t("hero_stat_brodo")}</div>
              </div>
              <div>
                <div className="font-display text-2xl md:text-3xl text-gold">100%</div>
                <div className="text-xs uppercase tracking-widest text-white/60 mt-1">{t("hero_stat_autentico")}</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <div className="h-10 w-px bg-gradient-to-b from-transparent via-white/50 to-transparent animate-pulse" />
      </div>
    </section>
  );
}
